import React, { useEffect, useState, useCallback, useMemo } from 'react';
import html2pdf from 'html2pdf.js';
import {
  Box, Typography, CircularProgress, Alert, Divider,
  FormControl, InputLabel, Select, MenuItem,
  Button, SelectChangeEvent, Grid, TextField,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ModuleHeader from './components/ModuleHeader';
import BotaoArquivar from './BotaoArquivar';
import { AVISO_D1 } from './arquivarCopia';

const API = '/api';

// ── Types ──────────────────────────────────────────────────────────────────
interface Area  { id: number; nome: string; zona: string }
interface Ciclo { id: number; nome: string }
interface Computado {
  residencias: number; comercios: number; terrenos_baldios: number;
  pontos_estrategicos: number; outros: number; total_imoveis: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recusados: number; fechados: number; recuperados: number;
  imoveis_positivos: number; dep_positivos: number; amostras_coletadas: number;
  quarteiroes_concluidos_count: number; quarteiroes_trabalhados_count: number;
  agentes_trabalhando: number; total_dias_trabalhados: number;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number; dep_total: number;
  dep_eliminados: number; dep_tratados: number;
  larvicida_l1_tipo: string | null; larvicida_l1_gramas: number;
  larvicida_l1_depositos: number; multiplos_l1: boolean;
  larvicida_l2_tipo: string | null; larvicida_l2_gramas: number;
  larvicida_l2_depositos: number; multiplos_l2: boolean;
  adulticida_tipo: string | null; adulticida_qtd_ml: number;
  adulticida_cargas: number; multiplos_adulticida: boolean;
  atividade_mais_freq: string | null; multiplas_atividades: boolean;
  ibi: number; iip: number; ib: number;
  lab_dep_aeg: Record<string, number>; lab_dep_alb: Record<string, number>;
  lab_imov_aeg: Record<string, number>; lab_imov_alb: Record<string, number>;
  lab_ex_aeg: Record<string, number>;  lab_ex_alb: Record<string, number>;
  lab_qrt_aegypti: string[]; lab_qrt_albopictus: string[]; lab_qrt_intersecao: string[];
}

interface Manual {
  id: number | null;
  atividade: string | null;
  imoveis_programados: number;
  agentes_programados: number;
  quarteiroes_programados: number;
  visto_supervisor: string | null;
  visto_data: string | null;
  pendencias: string | null;
}

interface DadosFAD07 {
  se: number;
  data_inicio: string;
  data_fim: string;
  total_registros: number;
  area: Area | null;
  ciclo: Ciclo | null;
  computado: Computado;
  manual: Manual;
}

interface DadosManuais {
  tipo: string;
  data_inicio: string;
  data_final: string;
  concluido: string;
  atividade: string;
  localidade: string;
  zona: string;
  ciclo_ano: string;
  sem_epidem: string;
  lab_dep_aeg_a1: number; lab_dep_aeg_a2: number; lab_dep_aeg_b: number; lab_dep_aeg_c: number;
  lab_dep_aeg_d1: number; lab_dep_aeg_d2: number; lab_dep_aeg_e: number; lab_dep_aeg_total: number;
  lab_dep_alb_a1: number; lab_dep_alb_a2: number; lab_dep_alb_b: number; lab_dep_alb_c: number;
  lab_dep_alb_d1: number; lab_dep_alb_d2: number; lab_dep_alb_e: number; lab_dep_alb_total: number;
  lab_imov_aeg_r: number; lab_imov_aeg_c: number; lab_imov_aeg_tb: number; lab_imov_aeg_pe: number;
  lab_imov_aeg_o: number; lab_imov_aeg_total: number; lab_imov_aeg_larvas: number; lab_imov_aeg_pupas: number;
  lab_imov_aeg_exuvia: number; lab_imov_aeg_adultos: number;
  lab_imov_alb_r: number; lab_imov_alb_c: number; lab_imov_alb_tb: number; lab_imov_alb_pe: number;
  lab_imov_alb_o: number; lab_imov_alb_total: number; lab_imov_alb_larvas: number; lab_imov_alb_pupas: number;
  lab_imov_alb_exuvia: number; lab_imov_alb_adultos: number;
  lab_imov_outros_r: number; lab_imov_outros_c: number; lab_imov_outros_tb: number; lab_imov_outros_pe: number;
  lab_imov_outros_o: number; lab_imov_outros_total: number; lab_imov_outros_larvas: number; lab_imov_outros_pupas: number;
  lab_imov_outros_exuvia: number; lab_imov_outros_adultos: number;
  lab_quart_aeg: string[];
  lab_quart_alb: string[];
  lab_quart_aeg_alb: string[];
  visto_supervisor: string;
  data_visto: string;
}

const DADOS_MANUAIS_DEFAULT: DadosManuais = {
  tipo: '',
  data_inicio: '',
  data_final: '',
  concluido: '',
  atividade: '2',
  localidade: '',
  zona: '',
  ciclo_ano: '',
  sem_epidem: '',
  lab_dep_aeg_a1: 0, lab_dep_aeg_a2: 0, lab_dep_aeg_b: 0, lab_dep_aeg_c: 0,
  lab_dep_aeg_d1: 0, lab_dep_aeg_d2: 0, lab_dep_aeg_e: 0, lab_dep_aeg_total: 0,
  lab_dep_alb_a1: 0, lab_dep_alb_a2: 0, lab_dep_alb_b: 0, lab_dep_alb_c: 0,
  lab_dep_alb_d1: 0, lab_dep_alb_d2: 0, lab_dep_alb_e: 0, lab_dep_alb_total: 0,
  lab_imov_aeg_r: 0, lab_imov_aeg_c: 0, lab_imov_aeg_tb: 0, lab_imov_aeg_pe: 0,
  lab_imov_aeg_o: 0, lab_imov_aeg_total: 0, lab_imov_aeg_larvas: 0, lab_imov_aeg_pupas: 0,
  lab_imov_aeg_exuvia: 0, lab_imov_aeg_adultos: 0,
  lab_imov_alb_r: 0, lab_imov_alb_c: 0, lab_imov_alb_tb: 0, lab_imov_alb_pe: 0,
  lab_imov_alb_o: 0, lab_imov_alb_total: 0, lab_imov_alb_larvas: 0, lab_imov_alb_pupas: 0,
  lab_imov_alb_exuvia: 0, lab_imov_alb_adultos: 0,
  lab_imov_outros_r: 0, lab_imov_outros_c: 0, lab_imov_outros_tb: 0, lab_imov_outros_pe: 0,
  lab_imov_outros_o: 0, lab_imov_outros_total: 0, lab_imov_outros_larvas: 0, lab_imov_outros_pupas: 0,
  lab_imov_outros_exuvia: 0, lab_imov_outros_adultos: 0,
  lab_quart_aeg:     Array(20).fill(''),
  lab_quart_alb:     Array(20).fill(''),
  lab_quart_aeg_alb: Array(20).fill(''),
  visto_supervisor:  '',
  data_visto:        new Date().toISOString().slice(0, 10),
};

// ── Helpers ────────────────────────────────────────────────────────────────
const spLabel = (num: number | string, text: string) => (
  <><sup style={{ color: '#9E9E9E', fontSize: '0.72em' }}>{num}</sup>{' '}{text}</>
);

const atividadeLabel = spLabel(12, 'Atividade');

const ATIVIDADES = [
  { value: '1', label: '1 - LI - Levantamento de índice' },
  { value: '2', label: '2 - LI+T - Levantamento de índice+Tratamento' },
  { value: '3', label: '3 - PE - Ponto estratégico' },
  { value: '4', label: '4 - T - Tratamento' },
  { value: '5', label: '5 - DF - Delimitação de Foco' },
  { value: '6', label: '6 - PVE - Pesquisa Vetorial Especial' },
];

const roTabNums = { readOnly: true as const, style: { fontVariantNumeric: 'tabular-nums' as const } };

// ── Component ──────────────────────────────────────────────────────────────
export default function FAD07({ onVoltar }: { onVoltar?: () => void }) {
  // Reference data
  const [areas,   setAreas]   = useState<Area[]>([]);
  const [ciclos,  setCiclos]  = useState<Ciclo[]>([]);
  // Filter selections
  const [areaId,  setAreaId]  = useState<string>('');
  const [cicloId, setCicloId] = useState<string>('');

  // Data state
  const [dadosFAD07,   setDadosFAD07]   = useState<DadosFAD07 | null>(null);
  const [dadosManuais, setDadosManuais] = useState<DadosManuais>(DADOS_MANUAIS_DEFAULT);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [labExpandido, setLabExpandido] = useState(true);

  // Arquivos: dados do documento para o botão "Arquivar"
  const pedidoArquivo = useMemo(() => {
    if (!dadosFAD07 || !areaId || !cicloId) return null;
    const nomeArea = areas.find(a => String(a.id) === String(areaId))?.nome ?? `Área ${areaId}`;
    const nomeCiclo = ciclos.find(c => String(c.id) === String(cicloId))?.nome ?? `Ciclo ${cicloId}`;
    return {
      pasta: 'fad07_geral' as const,
      tipo: 'fad07_geral',
      chave: `fad07_geral|c${cicloId}|a${areaId}`,
      titulo: `FAD-07 Geral · ${nomeArea} · ${nomeCiclo}`,
      ciclo_id: Number(cicloId),
      area_id: Number(areaId),
      referencia: nomeCiclo,
      avisos: [AVISO_D1],
    };
  }, [dadosFAD07, areaId, cicloId, areas, ciclos]);

  // ── Load reference data on mount ─────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/areas/`)
      .then(r => r.json())
      .then(setAreas)
      .catch(() => {});
    fetch(`${API}/ciclos/`)
      .then(r => r.json())
      .then(setCiclos)
      .catch(() => {});
  }, []);

  // ── Fetch FAD07 data ──────────────────────────────────────────────────────
  const fetchDados = useCallback(() => {
    if (!areaId || !cicloId) return;
    setLoading(true);
    setError('');
    const qs = new URLSearchParams({ area_id: areaId, ciclo_id: cicloId });
    fetch(`${API}/fad07-geral/dados?${qs}`)
      .then(r => {
        if (!r.ok) throw new Error(`Erro ${r.status}: ${r.statusText}`);
        return r.json();
      })
      .then((data: DadosFAD07) => {
        setDadosFAD07(data);
        console.log('DADOS FAD07:', data);
        const area  = areas.find(a => a.id === parseInt(areaId));
        const ciclo = ciclos.find(c => c.id === parseInt(cicloId));
        setDadosManuais(prev => ({
          ...DADOS_MANUAIS_DEFAULT,
          localidade: area?.nome  || '',
          zona:       area?.zona  || '',
          ciclo_ano:  ciclo?.nome || '',
          tipo:       prev.tipo || '1-sede',
        }));
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [areaId, cicloId, areas, ciclos]);

  const setManual = (field: keyof DadosManuais) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDadosManuais(prev => ({ ...prev, [field]: e.target.value }));

  const setManualSelect = (field: keyof DadosManuais) =>
    (e: SelectChangeEvent) =>
      setDadosManuais(prev => ({ ...prev, [field]: e.target.value }));

  const todosPreenchidos = Boolean(areaId && cicloId);

  const handleImprimir = () => window.print();

  const handleBaixarPDF = () => {
    const elemento = document.getElementById('fad07-papel');
    if (!elemento) return;
    html2pdf().set({
      margin: [12, 10, 12, 10],
      filename: `FAD07_${dadosManuais.localidade || 'area'}_${dadosManuais.sem_epidem || 'se'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(elemento).save();
  };

  const handleSalvar = async () => {
    try {
      const res = await fetch(`${API}/fad07/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ area_id: areaId, ciclo_id: cicloId, ...dadosManuais }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail ?? `Erro ${res.status}: ${res.statusText}`);
      }
      alert('Dados salvos com sucesso');
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Erro ao salvar');
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, bgcolor: '#F2F2F7', minHeight: '100vh' }}>
      <style>{`
        @page { size: A4 portrait; margin: 12mm 10mm; }
        @media print {
          body { background: #ffffff !important; }
          .MuiAppBar-root, .MuiDrawer-root { display: none !important; }
          .noprint { display: none !important; }
        }
      `}</style>
      <ModuleHeader
        titulo="FAD-07 Geral"
        subtitulo="Consolidado geral por área e ciclo"
        icone={<SummarizeIcon sx={{ fontSize: 28 }} />}
        accentColor="#00838F"
        onVoltar={onVoltar}
      />

      {/* ── Faixa de filtros ── */}
      <Box className="noprint" sx={{
        display: 'flex', flexWrap: 'wrap', gap: 2,
        alignItems: 'center', mb: 3,
        p: 2, background: '#fff', borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Área</InputLabel>
          <Select
            value={areaId}
            label="Área"
            onChange={(e: SelectChangeEvent) => setAreaId(e.target.value)}
          >
            <MenuItem value=""><em>— Selecione —</em></MenuItem>
            {areas.map(a => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.nome} ({a.zona})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Ciclo</InputLabel>
          <Select
            value={cicloId}
            label="Ciclo"
            onChange={(e: SelectChangeEvent) => setCicloId(e.target.value)}
          >
            <MenuItem value=""><em>— Selecione —</em></MenuItem>
            {ciclos.map(c => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchDados}
          disabled={!todosPreenchidos || loading}
          sx={{ height: 40 }}
        >
          Atualizar dados
        </Button>
      </Box>

      <Box className="noprint" sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSalvar}>
          Salvar dados manuais
        </Button>
        <Button variant="outlined" onClick={handleImprimir}>
          Imprimir
        </Button>
        <Button variant="outlined" onClick={handleBaixarPDF}>
          Baixar PDF
        </Button>
        <BotaoArquivar alvo={() => document.getElementById('fad07-papel')} pedido={pedidoArquivo} disabled={loading} />
      </Box>

      {/* ── Conteúdo condicional ── */}
      {!todosPreenchidos && !dadosFAD07 && (
        <Alert severity="info" sx={{ maxWidth: 520 }}>
          Selecione Área e Ciclo para gerar o FAD-07 Geral.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, maxWidth: 520 }}>
          {error}
        </Alert>
      )}

      {dadosFAD07 && !loading && (
        <Box id="fad07-papel" sx={{
          mt: 3,
          maxWidth: 794,
          mx: 'auto',
          p: '48px',
          background: '#fff',
          border: '1px solid #E0E0E0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
          borderRadius: 1,
        }}>
          <Typography variant="h6" align="center" sx={{ fontWeight: 700, letterSpacing: 0.5, mb: 0.5 }}>
            RESUMO GERAL DO SERVIÇO ANTIVETORIAL
          </Typography>
          <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary', letterSpacing: 0.3, mb: 3 }}>
            FAD-07 GERAL · PNCD
          </Typography>

          {/* ── (02–05) Identificação ── */}
          <Grid container spacing={2}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth label="(02) Município"
                value="Milagres"
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                select size="small" fullWidth label="(03) Localidade"
                value={dadosManuais.localidade}
                onChange={setManual('localidade')}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                {areas.map(a => (
                  <MenuItem key={a.id} value={a.nome}>{a.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={3}>
              <TextField
                select size="small" fullWidth label="(04) Zona"
                value={dadosManuais.zona}
                onChange={setManual('zona')}
                slotProps={{ inputLabel: { shrink: true } }}
              >
                <MenuItem value="Urbana">Urbana</MenuItem>
                <MenuItem value="Rural">Rural</MenuItem>
              </TextField>
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth label="(05) Categ"
                value="BRR"
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
          </Grid>

          {/* ── (06–11) Período / controle ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth
                label="(06) Tipo"
                value={dadosManuais.tipo}
                onChange={setManual('tipo')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                <MenuItem value="1-sede">1-sede</MenuItem>
                <MenuItem value="2-outros">2-outros</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth label="(07) Ciclo/Ano"
                value={dadosManuais.ciclo_ano}
                onChange={setManual('ciclo_ano')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {ciclos.map(c => (
                  <MenuItem key={c.id} value={c.nome}>{c.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                size="small" fullWidth label="(08) Data início"
                type="date"
                value={dadosManuais.data_inicio}
                onChange={setManual('data_inicio')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                size="small" fullWidth label="(09) Data final"
                type="date"
                value={dadosManuais.data_final}
                onChange={setManual('data_final')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                select size="small" fullWidth
                label="(10) Concluído"
                value={dadosManuais.concluido}
                onChange={setManual('concluido')}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                <MenuItem value="S">S</MenuItem>
                <MenuItem value="N">N</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4 }}>
              <TextField
                size="small" fullWidth label="(11) Período"
                value={''}
                slotProps={{ htmlInput: { readOnly: true }, inputLabel: { shrink: true } }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
          </Grid>

          {/* ── (12) Atividade ── */}
          <Box sx={{ mt: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="atividade-label">{atividadeLabel}</InputLabel>
              <Select
                labelId="atividade-label"
                value={dadosManuais.atividade}
                label={atividadeLabel}
                onChange={setManualSelect('atividade')}
              >
                {ATIVIDADES.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ── RESUMO DO TRABALHO DE CAMPO ── */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
            RESUMO DO TRABALHO DE CAMPO
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* ── (14–19) Imóveis por tipo ── */}
          <Grid container spacing={2}>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(14, 'Residência')}
                value={dadosFAD07.computado.residencias ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(15, 'Comércio')}
                value={dadosFAD07.computado.comercios ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(16, 'TB')}
                value={dadosFAD07.computado.terrenos_baldios ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(17, 'PE')}
                value={dadosFAD07.computado.pontos_estrategicos ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(18, 'Outro')}
                value={dadosFAD07.computado.outros ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={2}>
              <TextField
                size="small" fullWidth
                label={spLabel(19, 'Total')}
                value={dadosFAD07.computado.total_imoveis ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (13, 20–22) Quarteirões / Visitas ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(13, 'Total quart. concl.')}
                value={dadosFAD07.computado.quarteiroes_concluidos_count ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(20, 'Trat.Focal')}
                value={dadosFAD07.computado.trat_focal ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(21, 'Trat.Perifocal')}
                value={dadosFAD07.computado.trat_perifocal ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(22, 'Inspecionados')}
                value={dadosFAD07.computado.inspecionados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (23–26) Amostras / Situação ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(23, 'Amostras Coletadas')}
                value={dadosFAD07.computado.amostras_coletadas ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(24, 'Recusa')}
                value={dadosFAD07.computado.recusados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(25, 'Fechados')}
                value={dadosFAD07.computado.fechados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(26, 'Recuperados')}
                value={dadosFAD07.computado.recuperados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (27–34) Depósitos por tipo ── */}
          <Typography variant="caption" sx={{
            display: 'block', mt: 3, fontWeight: 600,
            color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Nº depósitos inspecionados por tipo
          </Typography>
          <Grid container spacing={1}>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(27, 'A1')}
                value={dadosFAD07.computado.dep_a1 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(28, 'A2')}
                value={dadosFAD07.computado.dep_a2 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(29, 'B')}
                value={dadosFAD07.computado.dep_b ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(30, 'C')}
                value={dadosFAD07.computado.dep_c ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(31, 'D1')}
                value={dadosFAD07.computado.dep_d1 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(32, 'D2')}
                value={dadosFAD07.computado.dep_d2 ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(33, 'E')}
                value={dadosFAD07.computado.dep_e ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                size="small" fullWidth
                label={spLabel(34, 'Total')}
                value={dadosFAD07.computado.dep_total ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (35–38) Depósito / Larvicida ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(35, 'Depósito Eliminado')}
                value={dadosFAD07.computado.dep_eliminados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(36, 'Tipo Larvicida')}
                value={dadosFAD07.computado.larvicida_l1_tipo ?? ''}
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(37, 'Qtde Gramas')}
                value={Number(dadosFAD07.computado.larvicida_l1_gramas ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(38, 'Qtde Dep.Trat.')}
                value={dadosFAD07.computado.larvicida_l1_depositos ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── (39–44) Adulticida / Agentes ── */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(39, 'Adulticida Tipo')}
                value={dadosFAD07.computado.adulticida_tipo ?? ''}
                slotProps={{ htmlInput: { readOnly: true } }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(40, 'Adulticida Qtde cargas')}
                value={dadosFAD07.computado.adulticida_cargas ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(43, 'Total de Agentes na Semana')}
                value={dadosFAD07.computado.agentes_trabalhando ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
            <Grid size={3}>
              <TextField
                size="small" fullWidth
                label={spLabel(44, 'Total de Dias trabalhados')}
                value={dadosFAD07.computado.total_dias_trabalhados ?? 0}
                slotProps={{ htmlInput: roTabNums }}
              />
            </Grid>
          </Grid>

          {/* ── Nota de instrução ── */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block', lineHeight: 1.5 }}>
              <strong>Atenção!</strong><br />
              37 - Inserir a quantidade de larvicida em gramas (ex. 2 cargas = 1.000g)<br />
              43 - Total de Agentes na semana (Ex. Seg=1, Ter=1, Qua=1, Qui=1, Sex=1 Total=5)<br />
              44 - Total de Dias trabalhados na semana (Ex. Seg=1, Ter=1, Qua=1, Qui=1, Sex=1 Total=5)
            </Typography>
          </Box>

          {/* ── Accordion Laboratório ── */}
          <Accordion
            expanded={labExpandido}
            defaultExpanded={true}
            onChange={() => setLabExpandido(prev => !prev)}
            sx={{ mt: 4 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Resumo do Laboratório (preencher quando os resultados chegarem)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                Nº depósitos com espécimes por tipo (45-52)
              </Typography>
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {['A1','A2','B','C','D1','D2','E','Total'].map(h => (
                      <TableCell key={h} align="center"><strong>{h}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap', pr: 2 }}>com Aedes aegypti</TableCell>
                    {(['lab_dep_aeg_a1','lab_dep_aeg_a2','lab_dep_aeg_b','lab_dep_aeg_c','lab_dep_aeg_d1','lab_dep_aeg_d2','lab_dep_aeg_e','lab_dep_aeg_total'] as (keyof DadosManuais)[]).map(f => (
                      <TableCell key={f} align="center" sx={{ p: 0.5 }}>
                        <TextField
                          type="number" size="small"
                          value={dadosManuais[f]}
                          onChange={setManual(f)}
                          slotProps={{ htmlInput: { min: 0, style: { textAlign: 'center', width: 44, minWidth: 44 } } }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap', pr: 2 }}>com Aedes albopictus</TableCell>
                    {(['lab_dep_alb_a1','lab_dep_alb_a2','lab_dep_alb_b','lab_dep_alb_c','lab_dep_alb_d1','lab_dep_alb_d2','lab_dep_alb_e','lab_dep_alb_total'] as (keyof DadosManuais)[]).map(f => (
                      <TableCell key={f} align="center" sx={{ p: 0.5 }}>
                        <TextField
                          type="number" size="small"
                          value={dadosManuais[f]}
                          onChange={setManual(f)}
                          slotProps={{ htmlInput: { min: 0, style: { textAlign: 'center', width: 44, minWidth: 44 } } }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1, display: 'block' }}>
                Nº de imóveis com espécies, por tipo + Nº de exemplares (53-62)
              </Typography>
              <Box sx={{ overflowX: 'auto', width: '100%' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {['Residência','Comércio','TB','PE','Outros','Total','Larvas','Pupas','Exúvia','Adultos'].map(h => (
                      <TableCell key={h} align="center"><strong>{h}</strong></TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {([
                    { label: 'com Aedes aegypti',    fields: ['lab_imov_aeg_r','lab_imov_aeg_c','lab_imov_aeg_tb','lab_imov_aeg_pe','lab_imov_aeg_o','lab_imov_aeg_total','lab_imov_aeg_larvas','lab_imov_aeg_pupas','lab_imov_aeg_exuvia','lab_imov_aeg_adultos'] },
                    { label: 'com Aedes albopictus',  fields: ['lab_imov_alb_r','lab_imov_alb_c','lab_imov_alb_tb','lab_imov_alb_pe','lab_imov_alb_o','lab_imov_alb_total','lab_imov_alb_larvas','lab_imov_alb_pupas','lab_imov_alb_exuvia','lab_imov_alb_adultos'] },
                    { label: 'Outros',                fields: ['lab_imov_outros_r','lab_imov_outros_c','lab_imov_outros_tb','lab_imov_outros_pe','lab_imov_outros_o','lab_imov_outros_total','lab_imov_outros_larvas','lab_imov_outros_pupas','lab_imov_outros_exuvia','lab_imov_outros_adultos'] },
                  ] as { label: string; fields: (keyof DadosManuais)[] }[]).map(row => (
                    <TableRow key={row.label}>
                      <TableCell sx={{ whiteSpace: 'nowrap', pr: 2 }}>{row.label}</TableCell>
                      {row.fields.map(f => (
                        <TableCell key={f} align="center" sx={{ p: 0.5 }}>
                          <TextField
                            type="number" size="small"
                            value={dadosManuais[f]}
                            onChange={setManual(f)}
                            slotProps={{ htmlInput: { min: 0, style: { textAlign: 'center', width: 44, minWidth: 44 } } }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1, display: 'block' }}>
                Nº e seq. dos quarteirões com Aedes aegypti (63)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                {Array.from({ length: 20 }, (_, i) => (
                  <TextField
                    key={i}
                    size="small"
                    placeholder="/"
                    value={dadosManuais.lab_quart_aeg[i]}
                    onChange={e => {
                      const novo = [...dadosManuais.lab_quart_aeg];
                      novo[i] = e.target.value;
                      setDadosManuais(prev => ({ ...prev, lab_quart_aeg: novo }));
                    }}
                    sx={{ width: '100%' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1, display: 'block' }}>
                Nº e seq. dos quarteirões com Aedes albopictus (64)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                {Array.from({ length: 20 }, (_, i) => (
                  <TextField
                    key={i}
                    size="small"
                    placeholder="/"
                    value={dadosManuais.lab_quart_alb[i]}
                    onChange={e => {
                      const novo = [...dadosManuais.lab_quart_alb];
                      novo[i] = e.target.value;
                      setDadosManuais(prev => ({ ...prev, lab_quart_alb: novo }));
                    }}
                    sx={{ width: '100%' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1, display: 'block' }}>
                Nº e seq. dos quarteirões com Aedes aegypti e Aedes albopictus (65)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                {Array.from({ length: 20 }, (_, i) => (
                  <TextField
                    key={i}
                    size="small"
                    placeholder="/"
                    value={dadosManuais.lab_quart_aeg_alb[i]}
                    onChange={e => {
                      const novo = [...dadosManuais.lab_quart_aeg_alb];
                      novo[i] = e.target.value;
                      setDadosManuais(prev => ({ ...prev, lab_quart_aeg_alb: novo }));
                    }}
                    sx={{ width: '100%' }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
          {/* ── (66–67) Visto do supervisor ── */}
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid size={6}>
              <TextField
                size="small" fullWidth
                label={spLabel(66, 'Visto do Supervisor')}
                value={dadosManuais.visto_supervisor}
                onChange={setManual('visto_supervisor')}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                size="small" fullWidth
                label={spLabel(67, 'Data do visto')}
                type="date"
                value={dadosManuais.data_visto}
                onChange={setManual('data_visto')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
          </Grid>

          <Typography variant="caption" sx={{ mt: 3, display: 'block', textAlign: 'center', fontSize: 10, color: 'text.disabled' }}>
            Documento gerado pelo sistema Tovis - criado por Walber Reis
          </Typography>
        </Box>
      )}
    </Box>
  );
}
